import classNames from 'classnames';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import React, { useState } from 'react';
import Routes from 'routes';

import useStores from 'utils/useStores';

import InputGroupComponent from 'components/groups/InputGroup';
import Fraction from '../models/Fraction';
import Icon from 'components/elements/Icon';
import Roasting from 'shared/Roasting';
import HumidityAnalysis from 'shared/HumidityAnalysis';
import { VARIANT } from 'constants/inputTypes';
import { addTestingIdentifier } from 'utils/testing';
import PhysicalAnalysis from 'decaf/components/PhysicalAnalysis';
import SplitFractionOverlay from './SplitFractionOverlay';
import SubdivisionEvaluation from './SubdivisionEvaluation';

export interface FractionsTableRowProps
  extends React.HTMLAttributes<HTMLTableRowElement> {
  fraction: Fraction;
  isMainFraction?: boolean;
  cuppingSessionEnabled?: boolean;
}

const ToggleData = styled.td`
  width: 1.5rem;
`;

const ToggleWrapper = styled.div`
  justify-content: center;
  display: flex;
  width: 1.5rem;
  cursor: pointer;
`;

const WeightWrapper = styled.td`
  min-width: 8.25rem;
`;

const IconWrapper = styled(Icon)`
  margin-left: 1rem;
  padding-bottom: 0.125rem;
`;

const RoastingWrapper = styled.div`
  .status {
    width: 4.5rem;
  }
`;

// PLA-860 v3 (D5): flecha de un grupo de subdivisiones, junto a su identificador.
const SubdivisionToggle = styled.span`
  display: inline-flex;
  margin-right: 0.375rem;
  vertical-align: middle;
  cursor: pointer;
`;

const SummaryWrapper = styled.small`
  display: block;
  font-weight: normal;
  color: #6b6b6b;
`;

const HintWrapper = styled.small`
  display: block;
  max-width: 11rem;
`;

const IdentifierWrapper = styled.td`
  min-width: 8rem;
`;

const InputGroupWrapper = styled(InputGroupComponent)`
  max-width: 11rem;
  padding: 0.3125em 0.5em !important;
  height: 3.25rem !important;
  text-align: right;
  input {
    width: auto;
  }
`;

const FractionsTableRow = ({
  fraction,
  isMainFraction = false,
  cuppingSessionEnabled,
  ...props
}: FractionsTableRowProps) => {
  // PLA-860 v2 (D2): las subdivisiones nuevas no tienen muestra (ni tostion ni analisis).
  const sample = fraction.sample;
  const current_roasting = sample?.current_roasting;
  const current_humidity_analysis = sample?.current_humidity_analysis;
  const current_qa_physical_analysis = sample?.current_qa_physical_analysis;
  const local_identifier = sample?.local_identifier;
  const initialSensorialAnalysis = sample?.current_sensorial_analysis;

  let current_sensorial_analysis = initialSensorialAnalysis;

  const { fractionsStore } = useStores();
  const [showSplitOverlay, setShowSplitOverlay] = useState(false);
  const [undoErrors, setUndoErrors] = useState<string[]>([]);
  const cuppingTableIdentifier = () => {
    if (
      !initialSensorialAnalysis ||
      initialSensorialAnalysis.finalized ||
      !initialSensorialAnalysis.cuppingTableIdentifier
    ) {
      return false;
    }
    return initialSensorialAnalysis.cuppingTableIdentifier;
  };
  if (cuppingSessionEnabled && !current_sensorial_analysis?.finalized) {
    current_sensorial_analysis = undefined;
  }

  // PLA-860 v2 (D1): "dividir" abre el popup por sacos / por cantidad de fracciones.
  const onSplitClick = () => {
    setShowSplitOverlay(true);
  };

  // PLA-860 v3 (W4): la fila dividida se abre, en cualquier nivel.
  const onSplitDone = () => {
    setShowSplitOverlay(false);

    if (fraction.subfractions.length > 0) {
      fraction.setExpanded(true);
    }
  };

  // PLA-860 v3 (W5, D6): resumen de las subdivisiones de una fila contraida.
  const renderSubdivisionSummary = () => {
    const summary = fraction.subdivisionSummary;

    if (fraction.expanded || !summary) {
      return null;
    }

    return (
      <SummaryWrapper {...addTestingIdentifier('subdivision_summary')}>
        {I18n.translate('fractions.subdivision.summary', { count: summary.count })}
        {summary.rejectedCount > 0 &&
          ` ${I18n.translate('fractions.subdivision.summary_rejected', {
            count: summary.rejectedCount,
            weight: parseFloat(summary.rejectedWeight.toFixed(2)),
          })}`}
      </SummaryWrapper>
    );
  };

  const onUndoSplitClick = async () => {
    setUndoErrors(await fraction.undoSplit());
  };

  const onBlur = async () => {
    await fraction.updateFraction();
  };

  const setRejectedWeight = (weight: string) => {
    const newWeight = weight ? parseFloat(weight) : 0;
    fraction.setRejectedWeight(newWeight);
  };

  const handleOnRoastingPrimaryClick = (event: React.MouseEvent, values: {}) => {
    current_roasting?.synchronize(values);
  };

  const renderRoasting = () =>
    current_roasting && (
      <Roasting
        identifier={local_identifier || ''}
        results={fractionsStore.roastingResults}
        roasting={current_roasting}
        roastingSampleWeight={current_roasting.initial_weight}
        text={I18n.translate(
          `widgets.lot_status_table.${current_roasting.passed ? 'done' : 'failed'}`
        )}
        status={current_roasting.passed}
        icon={current_roasting.passed ? 'passed' : 'failed'}
        onPrimaryClick={handleOnRoastingPrimaryClick}
        showReroast={false}
        showEnterRoasting={!current_roasting.id}
        validations={fractionsStore.roastingValidations}
      />
    );

  const sensorialAnalysisCondition = () => {
    if (cuppingSessionEnabled)
      return current_sensorial_analysis && current_sensorial_analysis.finalized;
    return current_sensorial_analysis;
  };

  const renderSensorialAnalysis = () => {
    if (!sample || !current_roasting) {
      return null;
    }

    const buttonClasses = classNames('button', 'button--small', {
      'button--green': current_roasting.id && fraction.weight > 0,
      'button--disabled': !current_roasting.id || fraction.weight <= 0,
      'button--ghost-cancel':
        current_roasting.id &&
        fraction.weight > 0 &&
        fractionsStore.selectedSamples.includes(sample.id),
    });
    if (sensorialAnalysisCondition()) {
      return (
        <div
          className={`status status--no-pointer status--${current_sensorial_analysis?.status}`}
        >
          <span>{current_sensorial_analysis?.text}</span>
          <svg className='icon'>
            <use xlinkHref={`#${current_sensorial_analysis?.icon}`} />
          </svg>
        </div>
      );
    } else if (cuppingTableIdentifier()) {
      return (
        <p>
          <span> in progress </span>
          <br />
          <span>{`${cuppingTableIdentifier()}`}</span>
        </p>
      );
    } else if (!cuppingSessionEnabled) {
      return (
        <>
          {fractionsStore.selectedSamples.includes(sample.id) && (
            <input type='hidden' name={`samples[${sample.id}]`} value={sample.id} />
          )}
          <button type='button' className={buttonClasses} onClick={handleButtonClick}>
            {(fractionsStore.selectedSamples.includes(sample.id) &&
              I18n.translate('fractions.cancel_cupping')) ||
              I18n.translate('fractions.to_cupping')}
          </button>
        </>
      );
    }
  };

  const handleOnChangeWeight = (event: React.ChangeEvent<HTMLInputElement>) => {
    const parsedValue = parseFloat(event.target.value);

    fraction.setWeight(parsedValue);
  };

  const handleOnPrimaryClick = (event, values) => {
    current_humidity_analysis?.synchronize(values);
  };

  const handleButtonClick = () => {
    if (sample) {
      fractionsStore.toggleSelectedLot(sample.id);
    }
  };

  const renderHumidityAnalysis = () => {
    if (!sample || !current_humidity_analysis) {
      return null;
    }

    return (
      <HumidityAnalysis
        identifier={sample.local_identifier}
        onPrimaryClick={handleOnPrimaryClick}
        humidityAnalysis={
          current_humidity_analysis.id ? current_humidity_analysis : undefined
        }
        lotFailed={false}
        validations={current_humidity_analysis.validations}
        text={current_humidity_analysis.text}
        status={current_humidity_analysis.status}
        icon={current_humidity_analysis.icon}
        reasons={fractionsStore.humidityAnalysisAcceptingReasons}
      />
    );
  };

  const renderPhysicalAnalysis = () => {
    if (!sample || !current_qa_physical_analysis) {
      return null;
    }

    return (
      <PhysicalAnalysis
        physicalAnalysis={current_qa_physical_analysis}
        newUrl={Routes.new_milling_order_sample_excelso_physical_analysis_path(
          fraction.milling_order_id,
          sample.id
        )}
        editUrl={Routes.edit_milling_order_sample_excelso_physical_analysis_path(
          fraction.milling_order_id,
          sample.id,
          current_qa_physical_analysis.id || 0
        )}
      />
    );
  };

  const checkFractionStatus = (fraction) => {
    if (fraction.isMain) {
      return fractionsStore.remainingFractionWeight(fraction);
    }
  };

  return (
    <tr key={fraction.node_identifier} {...props}>
      <ToggleData>
        {isMainFraction && (
          <ToggleWrapper
            onClick={fraction.toggleExpanded}
            {...addTestingIdentifier('fraction_toggle')}
          >
            <Icon icon={`caret-${fraction.expanded ? 'up' : 'down'}`} color='#006F94' />
          </ToggleWrapper>
        )}
      </ToggleData>

      <IdentifierWrapper className='align-left'>
        {!isMainFraction && fraction.subfractions.length > 0 && (
          <SubdivisionToggle
            onClick={fraction.toggleExpanded}
            {...addTestingIdentifier('subdivision_toggle')}
          >
            <Icon icon={`caret-${fraction.expanded ? 'up' : 'down'}`} color='#006F94' />
          </SubdivisionToggle>
        )}
        <b>{fraction.displayIdentifier}</b>
        {fraction.hasUnfinishedReprocesses && (
          <IconWrapper icon='ring-filled' color='#EFD449' />
        )}
        {renderSubdivisionSummary()}
      </IdentifierWrapper>

      <WeightWrapper className='align-left'>
        <InputGroupWrapper
          name='excelso_weight'
          append='kg'
          placeholder=''
          onChange={handleOnChangeWeight}
          onBlur={onBlur}
          noMargin={true}
          value={fraction.weight}
          variant={VARIANT.DECIMAL}
          readOnly={fraction.excelsoInputDisabled}
          disabled={fraction.excelsoInputDisabled}
          step={0.01}
          // TODO: max's tag validation commented in order to test ATLAS-1162, we have to check ATLAS-727 validation functionality and fix it.
          max={checkFractionStatus(fraction)}
          // max={
          //   fraction.isMain
          //     ? fractionsStore.remainingFractionWeight(fraction)
          //     : fraction.maxGreen
          // }
        />
      </WeightWrapper>

      <WeightWrapper className='align-center'>
        {fraction.showRejectedInput && (
          <InputGroupWrapper
            name='rejected_weight'
            append='kg'
            placeholder=''
            onChange={(e) => setRejectedWeight(e.target.value)}
            onBlur={onBlur}
            value={fraction.remainingInputWeight}
            noMargin={true}
            variant={VARIANT.DECIMAL}
            readOnly={fraction.rejectedInputDisabled}
            disabled={fraction.rejectedInputDisabled}
            max={fraction.weight}
          />
        )}
        {fraction.showRejectedSumHint && (
          <HintWrapper
            className='text-gray'
            {...addTestingIdentifier('rejected_sum_hint')}
          >
            {I18n.translate('fractions.subdivision.rejected_sum_hint', {
              weight: fraction.rejectedSubdivisionsWeight,
            })}
          </HintWrapper>
        )}
      </WeightWrapper>

      <td></td>

      <td className='align-center'>
        <RoastingWrapper>{renderRoasting()}</RoastingWrapper>
      </td>
      <td className='align-center border-right-none'>
        {renderSensorialAnalysis()}
        {fraction.isSubdivisionLeaf && <SubdivisionEvaluation fraction={fraction} />}
      </td>

      <td className='align-center border-right-black'>
        {(fraction.isMain ? !!current_sensorial_analysis : true) &&
          fraction.showSplitButton && (
            <button
              onClick={onSplitClick}
              disabled={!fraction.isSplittable}
              className='button button--ghost button--small'
              type='button'
              {...addTestingIdentifier('split_fraction_button')}
            >
              {I18n.translate('fractions.split')}
            </button>
          )}
        {fraction.canUndoSplit && (
          <button
            onClick={onUndoSplitClick}
            disabled={fraction.requestPending}
            className='button button--ghost-cancel button--small'
            type='button'
            {...addTestingIdentifier('undo_split_button')}
          >
            {I18n.translate('fractions.subdivision.undo_split')}
          </button>
        )}
        {undoErrors.filter(Boolean).map((error) => (
          <HintWrapper key={error} className='text-red'>
            {error}
          </HintWrapper>
        ))}
        {showSplitOverlay && (
          <SplitFractionOverlay
            fraction={fraction}
            onClose={() => setShowSplitOverlay(false)}
            onSplit={onSplitDone}
          />
        )}
      </td>
      <td className='align-center'>{fraction.isMain && renderHumidityAnalysis()}</td>
      <td className='align-center'>{fraction.isMain && renderPhysicalAnalysis()}</td>
    </tr>
  );
};

export default observer(FractionsTableRow);
